import { FormGroup, Input, Progress, Radio, RadioItem, Spinner } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { useSingleEntity } from 'modules/domain/common/hooks'
import {
  UsContract,
  UsContractDTO,
  UsContractStatuses,
  UsContractTypes,
  UsDeliveryTypes,
} from 'modules/domain/usContracts/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormSelectShared from 'views/components/FormSelectShared'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { Controls, PricingTypeRadio } from './CommonFields'
import { FormCompany } from 'views/components/form/FormCompany'
import { USCompany } from 'modules/domain/company/types'
import styled from 'styled-components'
import { FormDatePicker } from 'views/components/form/FormDatePicker'
import { FormEnumSelect } from 'views/components/form/EnumSelect/FormEnumSelect'
import { PositionFields } from './PositionFields'
import { ShortFormControls } from 'views/styled/common'
import { ProductFuturesSymbol } from 'modules/domain/collection/types'

type Props = {
  refetch: (affected?: UsContractStatuses) => Promise<void>
  onClose: () => void
  contract: UsContract
}

export const MarkContainer = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`

export const UsContractEdit: FC<Props> = ({ contract, onClose, refetch }) => {
  const { t } = useTranslation('usContracts')
  const { formProgress, formHandler } = useAFormHandler()

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [, productSymbols] = useSingleEntity<ProductFuturesSymbol[]>(
    `${endpoints.futuresSymbols()}?product_id=${contract.product.id}`,
  )
  const [companyProgress, company] = useSingleEntity<USCompany>(
    selectedCompanyId ? endpoints.company(selectedCompanyId) : undefined,
  )

  const formik = useFormik<UsContractDTO>({
    initialValues: {
      contract_type: contract.contract_type,
      sales_person: contract.sales_person.id.toString(),
      company: contract.company.id.toString(),
      market_zone: contract.market_zone?.id.toString() ?? null,
      contract_date: contract.contract_date,
      product: contract.product.id.toString(),
      delivery_type: contract.delivery_type,
      pricing_type: contract.pricing_type,
      positions: contract.positions.map((position) => ({
        id: position.id,
        symbol: position.symbol,
        symbolObjectId:
          productSymbols?.find((s) => {
            console.log(s)
            return s.symbol === position.symbol
          })?.id ?? null,
        pricing_type: position.pricing_type,
        quantity: position.quantity.toString(),
        futures_price: position.futures_price?.toString(),
        basis: position.basis?.toString(),
        price: position.price?.toString(),
        freight_cost: position.freight_cost?.toString(),
        start_date: position.start_date,
        end_date: position.end_date,
        transport_mode: position.transport_mode,
      })),
    },
    enableReinitialize: true,
    onSubmit: formHandler(
      async (values: UsContractDTO) => {
        // disable some fields update on frontend for now, but leave them for display
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { contract_type, product, pricing_type, ...dto } = values
        // hack because symbol on backend simple string for now
        dto.positions.forEach((position) => {
          position.symbol = productSymbols?.find((p) => p.id == position.symbolObjectId)?.symbol ?? ''
        })
        return await apiClient.patch(endpoints.usContracts(contract.id), dto)
      },
      {
        onSuccess: () => {
          refetch?.()
          onClose()
        },
        onError: (error) => {
          const { errors } = error as any
          errors?.positions?.forEach((p) => (p.symbolObjectId = p.symbol))
          formik.setErrors(errors || {})
        },
      },
    ),
  })

  const onChangeCompany = (value: string) => {
    formik.setFieldValue('company', value)
    setSelectedCompanyId(value)
  }
  let addressString = ''
  if (company?.main_address) {
    addressString = company.main_address.short_address
  } else if (company) {
    addressString = t('noAddress')
  }

  return (
    <ResponsiveModal
      title={t('editModal.title')}
      id="editUsContractModal"
      customFooter={<Controls formik={formik} formProgress={formProgress} onClose={onClose} />}
      progress={formProgress}
      onClose={onClose}
      size="medium"
    >
      <SharedModalContent>
        <SharedModalInputs>
          <FormGroup
            error={formik.errors.contract_type}
            label={t('fields.type')}
            // TODO center
            style={{ alignItems: 'center' }}
          >
            <Radio
              onChange={(value) => formik.setFieldValue('contract_type', value)}
              value={formik.values.contract_type}
            >
              <RadioItem value={UsContractTypes.sale} label={t('contractTypes.sale')} disabled />
              <RadioItem value={UsContractTypes.purchase} label={t('contractTypes.purchase')} disabled />
            </Radio>
          </FormGroup>
          <FormSelectShared
            label={t('fields.salesPerson')}
            formik={formik}
            fieldName="sales_person"
            placeholder=""
            getLabel={(label) => label.full_name}
            endpoint={endpoints.coordinatorsShort()}
            searchField="search"
          />
          <FormCompany formik={formik} onChange={onChangeCompany} allowClear={false} />
          <FormGroup
            label={t('fields.address')}
            afterControlComponent={
              companyProgress == Progress.WORK && (
                <MarkContainer>
                  <Spinner size="small" />
                </MarkContainer>
              )
            }
          >
            <Input value={addressString} disabled />
          </FormGroup>
          <FormDatePicker
            formik={formik}
            label={t('fields.contractDate')}
            fieldName="contract_date"
            id="contract_date"
          />
          <FormSelectShared
            label={t('fields.product')}
            formik={formik}
            endpoint={endpoints.products()}
            simpleApiFormat
            getLabel={(dto) => dto.title}
            fieldName="product"
            disabled
          />
          <ShortFormControls>
            <FormEnumSelect
              label={t('fields.delivery')}
              formik={formik}
              fieldName="delivery_type"
              translationNameSpace="usContracts:deliveryTypes"
              enumType={UsDeliveryTypes}
            />
          </ShortFormControls>
          <PricingTypeRadio formik={formik} fieldName="pricing_type" disabled />
          <PositionFields formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
